import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowRight, CheckCircle, Shield, Users, TrendingUp } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [partners, setPartners] = useState([]);
  const [services, setServices] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [partnersRes, servicesRes] = await Promise.all([
          axios.get(`${API}/partners`),
          axios.get(`${API}/services`)
        ]);
        setPartners(partnersRes.data);
        setServices(servicesRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const features = [
    {
      icon: Shield,
      title: 'Trusted Expertise',
      description: 'Certified professionals with years of experience in tax preparation and planning.'
    },
    {
      icon: Users,
      title: 'Personalized Service',
      description: 'Choose your preferred tax preparer from our team of 11 expert professionals.'
    },
    {
      icon: TrendingUp,
      title: 'Maximize Returns',
      description: 'We find every deduction and credit to ensure you get the maximum refund.'
    },
    {
      icon: CheckCircle,
      title: 'Year-Round Support',
      description: 'We are here for you beyond tax season with ongoing support and guidance.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 lg:px-8 overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10">
          <div className="absolute top-20 right-0 w-96 h-96 bg-teal-100 rounded-full blur-3xl opacity-30" />
          <div className="absolute bottom-20 left-0 w-96 h-96 bg-amber-100 rounded-full blur-3xl opacity-30" />
          {/* Gold accent lines */}
          <div className="absolute top-40 left-10 w-32 h-0.5 bg-gradient-to-r from-transparent via-amber-400 to-transparent opacity-50" />
          <div className="absolute top-60 right-20 w-24 h-0.5 bg-gradient-to-r from-transparent via-amber-400 to-transparent opacity-50" />
        </div>

        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 animate-in fade-in slide-in-from-left duration-700">
              <div className="inline-block">
                <span className="px-4 py-2 bg-teal-50 text-teal-700 rounded-full text-sm font-semibold border border-teal-200">
                  Professional Tax Services
                </span>
              </div>
              <div className="relative">
                <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                  Takeover Tax Inc.
                </h1>
                <div className="absolute -bottom-2 left-0 w-24 h-1 bg-gradient-to-r from-amber-500 to-transparent"></div>
              </div>
              <p className="text-2xl text-teal-600 font-semibold italic">
                Where Expert Care Meets Your Financial Goals
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Experience stress-free tax preparation with our team of certified professionals. 
                We take over the complexity so you can focus on what matters most.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button size="lg" className="bg-teal-600 hover:bg-teal-700 text-white text-lg px-8 py-6 transition-all hover:scale-105 hover:shadow-lg">
                    Book Your Appointment
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/services">
                  <Button size="lg" variant="outline" className="border-2 border-teal-600 text-teal-600 hover:bg-teal-50 text-lg px-8 py-6 transition-all hover:scale-105">
                    Explore Services
                  </Button>
                </Link>
              </div>
            </div>

            <div className="relative animate-in fade-in slide-in-from-right duration-700">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_tax-pros-portal/artifacts/gova4dfg_company_logo.png"
                  alt="Takeover Tax Inc Logo"
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-teal-600/20 to-transparent" />
              </div>
              {/* Floating stat cards */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-4 animate-in fade-in slide-in-from-bottom duration-1000">
                <div className="text-3xl font-bold text-teal-600">11</div>
                <div className="text-sm text-gray-600">Expert Preparers</div>
              </div>
              <div className="absolute -top-6 -right-6 bg-white rounded-xl shadow-xl p-4 animate-in fade-in slide-in-from-top duration-1000">
                <div className="text-3xl font-bold text-amber-500">100%</div>
                <div className="text-sm text-gray-600">Satisfaction</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4 relative inline-block">
              Why Choose Takeover Tax?
              <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-20 h-0.5 bg-gradient-to-r from-transparent via-amber-500 to-transparent"></div>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto mt-6">
              With over 6 years of experience, we combine expertise, personalized service, and dedication to deliver exceptional tax solutions.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all hover:-translate-y-2 duration-300 bg-white relative overflow-hidden group">
                  <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-amber-100/30 to-transparent rounded-bl-full"></div>
                  <CardContent className="p-6 space-y-4">
                    <div className="h-12 w-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-lg flex items-center justify-center shadow-md">
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900">{feature.title}</h3>
                    <p className="text-gray-600">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Services
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive tax solutions tailored to your unique needs.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              const IconComponent = LucideIcons[service.icon];
              return (
                <Card key={service.id} className="border-2 border-gray-100 hover:border-teal-300 transition-all hover:shadow-lg group">
                  <CardContent className="p-6 space-y-4">
                    <div className="h-14 w-14 bg-teal-50 rounded-xl flex items-center justify-center group-hover:bg-teal-100 transition-colors">
                      {IconComponent && <IconComponent className="h-7 w-7 text-teal-600" />}
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900">{service.title}</h3>
                    <p className="text-gray-600">{service.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button size="lg" variant="outline" className="border-2 border-teal-600 text-teal-600 hover:bg-teal-50 transition-all hover:scale-105">
                View All Services
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Team Preview */}
      <section className="py-20 px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Meet Our Expert Team
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Led by founder Tamika Harvey, our team of 11 certified tax professionals brings unique expertise to serve you.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8">
            {partners.slice(0, 5).map((partner) => (
              <Card key={partner.id} className="border-none shadow-lg hover:shadow-xl transition-all hover:-translate-y-2 duration-300 bg-white overflow-hidden group">
                <div className="aspect-square overflow-hidden">
                  <img
                    src={partner.image}
                    alt={partner.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                </div>
                <CardContent className="p-4 text-center">
                  <h3 className="font-semibold text-gray-900">{partner.name}</h3>
                  <p className="text-sm text-teal-600">{partner.title}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/team">
              <Button size="lg" className="bg-teal-600 hover:bg-teal-700 text-white transition-all hover:scale-105">
                Meet All Our Professionals
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 lg:px-8 bg-gradient-to-br from-teal-600 to-teal-700 text-white">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 className="text-4xl lg:text-5xl font-bold">
            Ready to Take Control of Your Taxes?
          </h2>
          <p className="text-xl text-teal-50">
            Book your appointment today and experience the Takeover Tax difference.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-teal-600 hover:bg-gray-100 text-lg px-8 py-6 transition-all hover:scale-105 hover:shadow-xl">
              Schedule Your Consultation
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
